import React, { useState, useEffect, useCallback } from 'react';
import { useLoading } from '../../../contexts/LoadingContext';
import apiClient from '../../../utils/apiClient';
import { ReactComponent as Logo } from '../../../assets/images/popashot_logo.svg';
import ErrorMessage from '../../common/ErrorMessage';
import LoadingButton from '../../common/LoadingButton';

const VerificationStep = ({ email, onVerificationSuccess, handleLoginSuccess }) => {
    const [verificationCode, setVerificationCode] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const { startLoading, stopLoading } = useLoading();
    const [verificationStatus, setVerificationStatus] = useState('idle');
    const [isFocused, setIsFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const handleSubmit = useCallback(async (e) => {
        e?.preventDefault();

        // Prevent submission if already loading
        if (verificationStatus === 'loading') {
            return;
        }

        setError('');
        setSuccessMessage('');
        setVerificationStatus('loading');
        startLoading('verification');

        try {
            const verificationData = {
                code: verificationCode,
                email: email
            };

            const response = await apiClient.post('/api/users/verify-email/', verificationData);
            setVerificationStatus('success');
            onVerificationSuccess(response.data);

        } catch (error) {
            setVerificationStatus('idle');
            stopLoading('verification');
            setError(error.response?.data?.detail || 'Verification failed');
            setVerificationCode('');
            // Focus first input on error
            const inputs = document.querySelectorAll('input[type="text"]');
            if (inputs[0]) inputs[0].focus();
        }
    }, [verificationCode, email, onVerificationSuccess, startLoading, stopLoading, verificationStatus]);

    useEffect(() => {
        if (verificationCode.length === 6 && verificationStatus === 'idle') {
            handleSubmit();
        }
    }, [verificationCode]);

    const handleCodeChange = (value, index) => {
        const cleanValue = value.replace(/\D/g, '');

        if (cleanValue.length > 1) {
            // Handle paste event
            const pastedValue = cleanValue.slice(0, 6);
            setVerificationCode(pastedValue);
            if (pastedValue.length === 6) {
                setActiveIndex(5);
            } else {
                setActiveIndex(pastedValue.length);
            }
        } else {
            // Handle single digit input
            const newCode = verificationCode.split('');
            newCode[index] = cleanValue;
            setVerificationCode(newCode.join(''));

            if (cleanValue && index < 5) {
                setActiveIndex(index + 1);
            }
        }

        if (error && cleanValue) {
            setError('');
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
            setActiveIndex(index - 1);
            const newCode = verificationCode.slice(0, -1);
            setVerificationCode(newCode);
        }
    };

    const handleResendCode = async () => {
        setError('');
        setSuccessMessage('');

        setVerificationStatus('sending');
        try {
            await apiClient.post('/api/users/resend-verification/', { email });
            setVerificationCode('');
            setSuccessMessage('New verification code sent successfully!');
            setActiveIndex(0);
        } catch (error) {
            setError(error.response?.data?.detail || 'Failed to resend verification code');
        }
    };

    useEffect(() => {
        const inputs = document.querySelectorAll('input[type="text"]');
        if (inputs[activeIndex]) {
            inputs[activeIndex].focus();
        }
    }, [activeIndex]);

    return (
        <div className="space-y-8 bg-transparent">
            <div className="flex flex-col items-center space-y-8">
                <Logo className="w-48 h-auto" />

                <div className="w-full max-w-md space-y-8">
                    <div className="text-center space-y-4">
                        <h1>Verify your email</h1>
                        <p>Enter the 6-digit code sent to {email}</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="flex justify-between gap-1">
                            {Array(6).fill('').map((_, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength="6"
                                    value={verificationCode[index] || ''}
                                    onChange={(e) => handleCodeChange(e.target.value, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    onPaste={(e) => {
                                        e.preventDefault();
                                        const pastedData = e.clipboardData.getData('text');
                                        handleCodeChange(pastedData, index);
                                    }}
                                    className={`w-12 h-16 text-center text-2xl 
                                        bg-popashot-grey-light rounded-md
                                        ${index === activeIndex ? 'ring-2 ring-popashot-blue' : ''}`}
                                    required={index === 0}
                                />
                            ))}
                        </div>

                        <ErrorMessage error={error} />
                        <div className="space-y-6">
                            <LoadingButton
                                type="submit"
                                className="w-full btn-primary"
                                loadingKey="verification"
                            >
                                Verify
                            </LoadingButton>

                            <div className="text-center mt-4">
                                <p className="text-sm">
                                    Didn't receive an email? Try checking your junk folder.{' '}
                                    {successMessage ? (
                                        <span className="text-green-600">Email sent!</span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleResendCode}
                                            disabled={verificationStatus === 'sending'}
                                            className="text-popashot-blue hover:text-popashot-blue-dark underline"
                                        >
                                            {verificationStatus === 'sending' ? 'Sending...' : 'Resend code'}
                                        </button>
                                    )}
                                </p>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default VerificationStep;
